import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useSubscription } from "@apollo/client/react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { useSearchParams } from "react-router";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { Collapse } from "@/components/design/Collapse";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { FilePreviewTile } from "@/components/common/FilePreviewTile";
import { acceptsFile, ingestFiles, type RejectedFile } from "@/lib/file-ingest";
import { MAX_YAML_IMPORT_BYTES, formatMegabytes } from "@/lib/upload-limits";
import { errorClassification, logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { ALL_NOTIFICATION_TYPES } from "@/lib/notification-types";
import { Permission, useHasPermission, useHasSystemPermission } from "@/auth/access-permissions";
import {
  CockpitNotificationsDocument,
  FileImportBatchDocument,
  FileImportJobDocument,
  ImportJobDocument,
  ImportJobEventsDocument,
  NotificationEventsDocument,
  StartFileImportDocument,
  type CockpitNotificationsQuery,
  type FileImportJobFieldsFragment,
  type ImportJobFieldsFragment,
} from "@/graphql/generated/operations";
import {
  FileImportJobStatus,
  FileImportRoute,
  ImportJobStage,
  ImportJobStatus,
  ImportMode,
} from "@/graphql/generated/types";
import {
  buildFileImportPipelineSteps,
  buildImportPipelineSteps,
  isFileJobTerminal,
  isProgressAtLeastAsFresh,
  isTerminal,
  selectLatestImportJob,
  type ImportPipelineStep,
  type ImportPipelineStepId,
  type ImportPipelineStepState,
} from "./import-job-snapshots";
import { ImportUnitBreakdown, ImportWarnings } from "./ImportOutcome";
import { ImportPersonActions } from "./ImportPersonActions";
import { ImportOptionsPicker } from "./ImportOptionsPicker";
import { DEFAULT_IMPORT_OPTIONS, type ImportRunOptions } from "./import-run-options";
import { ImportSpecificationPanel, type RequestedAnchor } from "./ImportSpecificationPanel";
import { ImportProblemGuidance } from "./ImportProblemGuidance";
import { useImportSpecification } from "./use-import-specification";

// The shared drop takes all three file types; genesis performs the authoritative,
// fail-closed detection (extension + declared MIME + leading bytes must agree) and
// picks the route. The list here only shapes the picker and the client pre-check.
// The drop is the YAML importer only; CVs (PDF/DOCX) go through the page's own CV import.
// Genesis enforces the same contract server-side ("reject non-YAML files in importer intake").
const ACCEPT = ".yaml,.yml";
const YAML_ACCEPT = ".yaml,.yml";
// Temporary admission guard matching Genesis' current default capacity:
// 2 running jobs + 4 queued jobs. The durable-queue follow-up should remove
// this UI ceiling; until then it prevents a single idle-system batch from
// deterministically overflowing the executor.
const MAX_BATCH_FILES = 6;

interface StagedFile {
  name: string;
  size: number;
  mimeType: string;
  /** Base64 payload for the `Binary!` argument (no `data:` prefix). */
  content: string;
  /**
   * Client-side hint only, used to decide whether the YAML-only options are worth
   * offering. genesis remains the authority on the actual route.
   */
  looksLikeYaml: boolean;
}

/** One submitted file of the batch: its job is running server-side. */
interface SubmittedRun {
  fileJob: FileImportJobFieldsFragment;
  staged: StagedFile;
  options: ImportRunOptions;
}

/**
 * Terminal row outcome reported up by BatchRunResult. The parent's `submitted`
 * entries keep the status snapshot from the start mutation; only the rows
 * follow their job (poll/subscription), so batch-level decisions need this
 * report channel.
 */
type BatchOutcome = "success" | "mixed" | "error";

interface ActiveBatch {
  id: string;
  /** Jobs Genesis has acknowledged, never the number the browser merely intended to send. */
  accepted: number;
}

type Notification = CockpitNotificationsQuery["notifications"][number];

const SUPPORTED_NOTIFICATIONS = { supportedTypes: ALL_NOTIFICATION_TYPES };
const NOTIFICATION_QUERY_VARIABLES = {
  input: { take: 20, supportedTypes: ALL_NOTIFICATION_TYPES },
  supported: SUPPORTED_NOTIFICATIONS,
};

const STEP_LABELS: Record<ImportPipelineStepId, { id: string; defaultMessage: string }> = {
  file: { id: "service.page.bulkYaml.stageFile", defaultMessage: "File" },
  queue: { id: "service.page.bulkYaml.stageQueue", defaultMessage: "Queue" },
  import: { id: "service.page.bulkYaml.stageImport", defaultMessage: "Import" },
  result: { id: "service.page.bulkYaml.stageResult", defaultMessage: "Result" },
};

const STEP_STATE_LABELS: Record<ImportPipelineStepState, { id: string; defaultMessage: string }> = {
  pending: { id: "service.page.bulkYaml.stepStatePending", defaultMessage: "not started yet" },
  active: { id: "service.page.bulkYaml.stepStateActive", defaultMessage: "in progress" },
  done: { id: "service.page.bulkYaml.stepStateDone", defaultMessage: "done" },
  failed: { id: "service.page.bulkYaml.stepStateFailed", defaultMessage: "failed" },
};

const MODE_LABELS: Record<ImportMode, { id: string; defaultMessage: string }> = {
  [ImportMode.AllOrNothing]: {
    id: "service.page.bulkYaml.modeAllOrNothing",
    defaultMessage: "Abort completely",
  },
  [ImportMode.KeepSuccessful]: {
    id: "service.page.bulkYaml.modeKeepSuccessful",
    defaultMessage: "Keep successful",
  },
};

const ROUTE_LABELS: Record<FileImportRoute, { id: string; defaultMessage: string }> = {
  [FileImportRoute.YamlImport]: {
    id: "service.page.bulkYaml.routeYaml",
    defaultMessage: "YAML import",
  },
  [FileImportRoute.DocumentExtraction]: {
    id: "service.page.bulkYaml.routeExtraction",
    defaultMessage: "CV and PDF extraction",
  },
};

/**
 * Compact File -> Queue -> Import -> Result summary (see
 * import-job-snapshots.ts for how the four states are derived). Each node
 * carries both a color/glyph AND a localized text label + aria-label, so the
 * step is never communicated by color alone.
 */
function ImportPipelineBar({ steps, intl }: { steps: ImportPipelineStep[]; intl: IntlShape }) {
  return (
    <ol
      id="import-steps"
      className="mt-5 mb-4 grid grid-cols-4 gap-1"
      aria-label={intl.formatMessage({ id: "service.page.bulkYaml.pipelineLabel", defaultMessage: "Import steps" })}
    >
      {steps.map((step, index) => {
        const stepLabel = intl.formatMessage(STEP_LABELS[step.id]);
        const stateLabel = intl.formatMessage(STEP_STATE_LABELS[step.state]);
        const previous = steps[index - 1];
        const connected = index > 0 && (previous?.state === "done" || previous?.state === "failed");
        return (
          <li
            key={step.id}
            id={`import-step-${step.id}`}
            className="relative text-center"
            aria-current={step.state === "active" ? "step" : undefined}
          >
            {index > 0 ? (
              <span
                aria-hidden="true"
                className={`absolute right-1/2 top-3 h-px w-full ${connected ? "bg-accent" : "bg-ink/15"}`}
              />
            ) : null}
            <span
              id={`import-stepstate-${step.id}`}
              role="img"
              aria-label={`${stepLabel}: ${stateLabel}`}
              title={`${stepLabel}: ${stateLabel}`}
              className={`relative z-10 mx-auto flex h-6 w-6 items-center justify-center rounded-full text-smycles-micro font-medium ${
                step.state === "failed"
                  ? "bg-red-600 text-white"
                  : step.state === "done"
                    ? "bg-accent text-ink"
                    : step.state === "active"
                      ? "bg-surface text-ink-accent ring-2 ring-accent-decoration"
                      : "bg-surface text-ink-muted ring-1 ring-ink/20"
              }`}
            >
              {step.state === "done" ? "✓" : step.state === "failed" ? "✕" : index + 1}
            </span>
            <span
              className={`mt-1.5 block truncate text-smycles-label ${
                step.state === "active"
                  ? "font-semibold text-ink"
                  : step.state === "failed"
                    ? "font-medium text-red-600"
                    : "text-ink-muted"
              }`}
            >
              {stepLabel}
            </span>
            {step.state === "active" ? <span className="text-smycles-micro text-ink">{stateLabel}</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Polls a query while `active`, and stops otherwise.
 *
 * Apollo hands back fresh `startPolling` / `stopPolling` identities on every
 * render. Listing them as effect dependencies re-runs the effect on each render,
 * and `startPolling` fetches immediately, so with two polled queries on one screen
 * their mutual re-renders turn into a refetch cascade (it drains a test's mock
 * queue in milliseconds and hammers genesis in production). Holding the query in a
 * ref keeps the latest functions reachable without letting their identity trigger
 * the effect, so polling restarts only when the actual condition changes.
 */
function usePolling(
  query: { startPolling: (ms: number) => void; stopPolling: () => void },
  active: boolean,
  intervalMs = 2_000,
) {
  const ref = useRef(query);
  // Declared before the polling effect on purpose: effects run in declaration
  // order within a commit, so the ref already holds this render's query by the
  // time the effect below reads it.
  useEffect(() => {
    ref.current = query;
  });
  useEffect(() => {
    if (!active) {
      ref.current.stopPolling();
      return;
    }
    ref.current.startPolling(intervalMs);
    return () => ref.current.stopPolling();
  }, [active, intervalMs]);
}

function batchPipelineSteps(
  uploading: boolean,
  progress: Notification["progress"] | null | undefined,
  accepted: number,
): ImportPipelineStep[] {
  if (uploading) {
    return [
      { id: "file", state: "active" },
      { id: "queue", state: "pending" },
      { id: "import", state: "pending" },
      { id: "result", state: "pending" },
    ];
  }
  if (!progress || progress.done < Math.max(progress.total, accepted)) {
    return [
      { id: "file", state: "done" },
      { id: "queue", state: "done" },
      { id: "import", state: "active" },
      { id: "result", state: "pending" },
    ];
  }
  const resultState: ImportPipelineStepState = progress.failed > 0 ? "failed" : "done";
  return [
    { id: "file", state: "done" },
    { id: "queue", state: "done" },
    { id: "import", state: resultState },
    { id: "result", state: resultState },
  ];
}

/** Job id as a small, click-to-copy support code. Never shows job.message (@adr SPARK-0010-no-raw-server-errors-in-ui@1). */
function CopyableJobId({ jobId }: { jobId: string }) {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      // Ohne sicheren Kontext (http auf einem anderen Host als localhost) gibt es
      // keine Clipboard-API. Der Optional-Chain schluckte das und meldete Erfolg,
      // obwohl nichts kopiert wurde.
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(jobId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      logApplicationFailure("bulk-yaml-import.copy-job-id");
    }
  };

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={intl.formatMessage({ id: "service.page.bulkYaml.jobIdHint", defaultMessage: "Job ID, for support" })}
      className="mt-2 inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-smycles-micro text-ink-muted ring-1 ring-ink/10 transition hover:text-ink hover:ring-ink/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
    >
      <span className="font-mono">{jobId}</span>
      <span>
        {copied ? (
          <FormattedMessage id="design.copied" defaultMessage="Copied" />
        ) : (
          <FormattedMessage id="design.copy" defaultMessage="Copy" />
        )}
      </span>
    </button>
  );
}

/** Which worker genesis handed the file to. Always the backend's answer. */
function RouteBadge({ route, intl }: { route: FileImportRoute; intl: IntlShape }) {
  return (
    <span className="inline-flex items-center rounded bg-ink/[0.06] px-1.5 py-0.5 text-smycles-micro font-medium text-ink">
      {intl.formatMessage(ROUTE_LABELS[route])}
    </span>
  );
}

/**
 * The YAML route's result body: progress while it runs, then the outcome. The
 * dry-run and real-import wordings are deliberately disjoint, never a shared
 * sentence with a swapped adjective, so a simulation can't read as a saved import.
 */
function wasSchedulingRejected(job: ImportJobFieldsFragment | null | undefined): boolean {
  // The backend deliberately stores a plain, non-technical marker for the one
  // failure that happens before parsing. Map it to localized operator copy;
  // never render the backend message itself (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
  return job?.status === ImportJobStatus.Failed && job.message === "Import could not be scheduled";
}

function YamlJobBody({
  job,
  fileName,
  intl,
  onOpenDocumentation,
  showFileName = true,
}: {
  /** Null while the route is known but the richer job has not loaded yet. */
  job: ImportJobFieldsFragment | null;
  fileName: string;
  intl: IntlShape;
  onOpenDocumentation: (anchor: string) => void;
  showFileName?: boolean;
}) {
  const modeLabel = intl.formatMessage(MODE_LABELS[job?.mode ?? ImportMode.AllOrNothing]);
  // Before the job loads, the file is provably queued: `startFileImport` only
  // returns after the file was accepted and handed to the worker. Rendering that
  // through this same component (rather than a separate placeholder subtree) keeps
  // one stable DOM node, so the status text is updated in place instead of being
  // torn down and rebuilt underneath anything already reading it.
  const status = job?.status ?? ImportJobStatus.Queued;
  const schedulingRejected = wasSchedulingRejected(job);

  return (
    <div className="min-w-0">
      {showFileName ? <div className="smycles-field-value truncate font-medium">{job?.fileName ?? fileName}</div> : null}

      {status === ImportJobStatus.Queued ? (
        <div className="mt-0.5 text-smycles-meta text-ink">
          <FormattedMessage id="service.page.bulkYaml.queued" defaultMessage="Waiting for processing" />
        </div>
      ) : null}

      {job && status === ImportJobStatus.Running && job.stage === ImportJobStage.Indexing ? (
        <div className="mt-0.5 text-smycles-meta text-ink">
          <span>
            <FormattedMessage id="service.page.bulkYaml.indexing" defaultMessage="Checking the file …" />
          </span>
          {job.totalDocuments > 0 ? (
            <>
              {" · "}
              <span>
                <FormattedMessage
                  id="service.page.bulkYaml.documentsFound"
                  defaultMessage="{n, plural, one {# document} other {# documents}} found"
                  values={{ n: job.totalDocuments }}
                />
              </span>
            </>
          ) : null}
        </div>
      ) : null}

      {job && status === ImportJobStatus.Running && job.stage === ImportJobStage.Importing ? (
        <div id="yaml-import-progress" className="mt-0.5 text-smycles-meta text-ink">
          <FormattedMessage
            id="service.page.bulkYaml.progress"
            defaultMessage="{done} of {total} documents processed{kind, select, none {} other { · {kind}}}"
            values={{
              done: job.processedDocuments,
              total: job.totalDocuments,
              kind: job.currentKind ?? "none",
            }}
          />
        </div>
      ) : null}

      {job && status === ImportJobStatus.Completed ? (
        <div className="mt-1">
          <div className={`text-smycles-meta font-medium ${job.dryRun ? "text-ink" : "text-ink-accent"}`}>
            <span aria-hidden="true">✓ </span>
            <span>
              {job.dryRun ? (
                <FormattedMessage
                  id="service.page.bulkYaml.dryRunSuccessTitle"
                  defaultMessage="Validation successful"
                />
              ) : (
                <FormattedMessage id="service.page.bulkYaml.successTitle" defaultMessage="Import successful" />
              )}
            </span>
          </div>

          <div className="mt-0.5 text-smycles-label text-ink-muted">
            {intl.formatMessage(
              job.dryRun
                ? { id: "service.page.bulkYaml.runBadgeDry", defaultMessage: "Validation · {mode}" }
                : { id: "service.page.bulkYaml.runBadgeReal", defaultMessage: "Import · {mode}" },
              { mode: modeLabel },
            )}
          </div>

          {job.dryRun ? (
            <div id="yaml-import-rolledback" className="mt-1 text-smycles-meta text-ink">
              <FormattedMessage
                id="service.page.bulkYaml.dryRunNoSave"
                defaultMessage="No data was saved. The transaction was deliberately rolled back."
              />
            </div>
          ) : null}

          {job.totalDocuments > 0 ? (
            <div id="yaml-import-documentcount" className="mt-0.5 text-smycles-meta text-ink">
              <FormattedMessage
                id="service.page.bulkYaml.progress"
                defaultMessage="{done} of {total} documents processed{kind, select, none {} other { · {kind}}}"
                values={{ done: job.processedDocuments, total: job.totalDocuments, kind: "none" }}
              />
            </div>
          ) : null}

          <div className="mt-0.5 text-smycles-meta text-ink">
            {job.dryRun ? (
              <FormattedMessage
                id="service.page.bulkYaml.dryRunSummary"
                defaultMessage="{n, plural, one {# row} other {# rows}} would be affected"
                values={{ n: job.rowsAffected }}
              />
            ) : (
              <FormattedMessage
                id="service.page.bulkYaml.summary"
                defaultMessage="{n, plural, one {# row} other {# rows}} affected"
                values={{ n: job.rowsAffected }}
              />
            )}
          </div>

          <div className="mt-0.5 text-smycles-label text-ink-muted">
            <FormattedMessage
              id="service.page.bulkYaml.completedAt"
              defaultMessage="Completed {date}"
              values={{ date: intl.formatDate(job.updatedAt, { dateStyle: "medium", timeStyle: "short" }) }}
            />
          </div>
        </div>
      ) : null}

      {job && status === ImportJobStatus.Failed ? (
        <div id="yaml-import-failed" className="mt-1">
          <div className="text-smycles-meta font-medium text-red-600">
            <span aria-hidden="true">✕ </span>
            <span>
              {schedulingRejected ? (
                <FormattedMessage
                  id="service.page.bulkYaml.capacityRejected"
                  defaultMessage="The file could not be started because all import slots were occupied. It has not been checked yet."
                />
              ) : job.mode === ImportMode.KeepSuccessful ? (
                <FormattedMessage
                  id="service.page.bulkYaml.failedPartial"
                  defaultMessage="Import failed. Units that had already been committed were kept."
                />
              ) : (
                <FormattedMessage
                  id="service.page.bulkYaml.rolledBack"
                  defaultMessage="Import failed; all database changes were rolled back."
                />
              )}
            </span>
          </div>
          {/* job.message is genesis-internal detail (persistence/parser text) and is
              never rendered here (@adr SPARK-0010-no-raw-server-errors-in-ui@1); only the job id, as an opaque support code. */}
          <CopyableJobId jobId={job.id} />
          {job.problem ? (
            <ImportProblemGuidance
              problem={job.problem}
              onOpenDocumentation={onOpenDocumentation}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The extraction route's result body. Intentionally sparse: this page does not own
 * that pipeline, it only reports where the file went and links on to where the
 * extracted powers and skills actually appear.
 */
function ExtractionJobBody({
  fileJob,
  showFileName = true,
}: {
  fileJob: FileImportJobFieldsFragment;
  showFileName?: boolean;
}) {
  return (
    <div className="min-w-0">
      {showFileName ? <div className="smycles-field-value truncate font-medium">{fileJob.fileName}</div> : null}

      {fileJob.status === FileImportJobStatus.Queued ? (
        <div className="mt-0.5 text-smycles-meta text-ink">
          <FormattedMessage id="service.page.bulkYaml.queued" defaultMessage="Waiting for processing" />
        </div>
      ) : null}

      {fileJob.status === FileImportJobStatus.Running ? (
        <div className="mt-0.5 text-smycles-meta text-ink">
          <FormattedMessage
            id="service.page.bulkYaml.extractionRunning"
            defaultMessage="Extracting powers and skills …"
          />
        </div>
      ) : null}

      {fileJob.status === FileImportJobStatus.Completed ? (
        <div className="mt-1">
          <div className="text-smycles-meta font-medium text-ink-accent">
            <span aria-hidden="true">✓ </span>
            <FormattedMessage
              id="service.page.bulkYaml.extractionDone"
              defaultMessage="Extraction finished"
            />
          </div>
          <p className="mt-0.5 text-smycles-meta text-ink">
            <FormattedMessage
              id="service.page.bulkYaml.extractionResultHint"
              defaultMessage="The extracted powers and skills are in your own profile."
            />
          </p>
          <a
            href={fileJob.resultLink}
            className="mt-1 inline-block text-smycles-meta text-ink-accent underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
          >
            <FormattedMessage id="service.page.bulkYaml.extractionOpen" defaultMessage="Open result" />
          </a>
        </div>
      ) : null}

      {fileJob.status === FileImportJobStatus.Failed ? (
        <div className="mt-1">
          <div className="text-smycles-meta font-medium text-red-600">
            <span aria-hidden="true">✕ </span>
            <FormattedMessage
              id="service.page.bulkYaml.extractionFailed"
              defaultMessage="Extraction failed."
            />
          </div>
          <CopyableJobId jobId={fileJob.id} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Inline result for one file in a batch. Each bounded batch row follows its
 * job even while collapsed so the status dot stays trustworthy; the larger
 * result body is only rendered when the operator opens that row.
 */
function BatchRunResult({
  run,
  onRetry,
  onOpenDocumentation,
  onReport,
  openInitially = false,
  readOnly = false,
}: {
  run: SubmittedRun;
  onRetry: (run: SubmittedRun) => Promise<void>;
  onOpenDocumentation: (anchor: string) => void;
  onReport?: (jobId: string, outcome: BatchOutcome) => void;
  openInitially?: boolean;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const panelId = useId();
  const [open, setOpen] = useState(openInitially);
  const [retrying, setRetrying] = useState(false);
  const { fileJob: initialFileJob } = run;
  const yaml = initialFileJob.route === FileImportRoute.YamlImport;

  const fileJobQuery = useQuery(FileImportJobDocument, {
    variables: { jobId: initialFileJob.id },
    skip: yaml,
    fetchPolicy: "network-only",
  });
  const fileJob = fileJobQuery.data?.fileImportJob ?? initialFileJob;
  usePolling(fileJobQuery, !yaml && !isFileJobTerminal(fileJob.status));

  const jobQuery = useQuery(ImportJobDocument, {
    variables: { jobId: initialFileJob.id },
    skip: !yaml,
    fetchPolicy: "network-only",
  });
  const queriedJob = jobQuery.data?.importJob;
  const queryTerminal = isTerminal(queriedJob?.status);
  usePolling(jobQuery, yaml && !queryTerminal);
  const live = useSubscription(ImportJobEventsDocument, {
    variables: { jobId: initialFileJob.id },
    skip: !yaml || queryTerminal,
  });
  const job = selectLatestImportJob(initialFileJob.id, queriedJob, live.data?.importJobEvents);
  const terminal = yaml ? isTerminal(job?.status) : isFileJobTerminal(fileJob.status);
  const error = yaml ? jobQuery.error : fileJobQuery.error;
  const schedulingRejected = yaml && wasSchedulingRejected(job);
  const resultTone: BatchOutcome | "pending" = yaml
    ? job && terminal
      ? job.succeededUnits > 0 && (job.failedUnits > 0 || job.skippedUnits > 0)
        ? "mixed"
        : job.status === ImportJobStatus.Completed
          ? "success"
          : "error"
      : "pending"
    : isFileJobTerminal(fileJob.status)
      ? fileJob.status === FileImportJobStatus.Completed
        ? "success"
        : "error"
      : "pending";
  const resultTonePresentation = {
    pending: {
      className: "bg-ink/30",
      label: intl.formatMessage({ id: "service.page.bulkYaml.batchPending", defaultMessage: "Pending" }),
    },
    success: {
      className: "bg-emerald-500",
      label: intl.formatMessage({ id: "service.page.bulkYaml.batchSuccess", defaultMessage: "Successful" }),
    },
    mixed: {
      className: "bg-ink-attention",
      label: intl.formatMessage({ id: "service.page.bulkYaml.batchMixed", defaultMessage: "Partially successful" }),
    },
    error: {
      className: "bg-rose-500",
      label: intl.formatMessage({ id: "service.page.bulkYaml.batchFailed", defaultMessage: "Failed" }),
    },
  }[resultTone];

  useEffect(() => {
    if (resultTone !== "pending") onReport?.(initialFileJob.id, resultTone);
  }, [resultTone, initialFileJob.id, onReport]);

  return (
    <li className={`border-t border-ink/10 first:border-t-0 ${open ? "bg-accent/[0.035]" : ""}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="group flex min-h-11 w-full min-w-0 items-center gap-3 px-2 py-2 text-left transition-colors hover:bg-accent/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink-ui"
      >
        <span
          role="img"
          aria-label={resultTonePresentation.label}
          title={resultTonePresentation.label}
          className={`h-2 w-2 shrink-0 rounded-full ${resultTonePresentation.className}`}
        />
        <span className={`min-w-0 flex-1 truncate text-smycles-meta ${open ? "font-medium text-ink" : "text-ink"}`}>
          {initialFileJob.fileName}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 text-smycles-label text-ink-accent">
          <span aria-hidden="true" className={`transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}>
            ›
          </span>
          <span className="underline underline-offset-2">
            {open ? (
              <FormattedMessage id="service.page.bulkYaml.batchHide" defaultMessage="Hide result" />
            ) : (
              <FormattedMessage id="service.page.bulkYaml.batchOpen" defaultMessage="Show result" />
            )}
          </span>
        </span>
      </button>

      <Collapse open={open}>
        <div id={panelId} className="mb-2 ml-3.5 rounded-md bg-surface p-3 ring-1 ring-ink/10">
          {error ? <div className="text-smycles-meta text-red-600">{userErrorMessage(intl, error)}</div> : null}
          {yaml ? (
            <>
              <YamlJobBody
                job={job}
                fileName={initialFileJob.fileName}
                intl={intl}
                onOpenDocumentation={onOpenDocumentation}
                showFileName={false}
              />
              {job && terminal ? (
                <>
                  <ImportUnitBreakdown
                    key={job.id}
                    idPrefix={`import-job-${job.id}`}
                    units={job.units}
                    personActions={job.personActions}
                    dryRun={job.dryRun}
                    totalUnits={job.totalUnits}
                    succeededUnits={job.succeededUnits}
                    failedUnits={job.failedUnits}
                    skippedUnits={job.skippedUnits}
                    onOpenDocumentation={onOpenDocumentation}
                  />
                  <ImportWarnings warnings={job.warnings} />
                  <ImportPersonActions personActions={job.personActions} dryRun={job.dryRun} />
                </>
              ) : null}
              {schedulingRejected && !readOnly ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={retrying}
                  className="mt-3"
                  onClick={() => {
                    setRetrying(true);
                    void onRetry(run).finally(() => setRetrying(false));
                  }}
                >
                  {retrying ? (
                    <FormattedMessage id="service.page.bulkYaml.retrying" defaultMessage="Retrying …" />
                  ) : (
                    <FormattedMessage id="service.page.bulkYaml.retryFile" defaultMessage="Retry" />
                  )}
                </Button>
              ) : null}
            </>
          ) : (
            <ExtractionJobBody fileJob={fileJob} showFileName={false} />
          )}
        </div>
      </Collapse>
    </li>
  );
}

/**
 * Collapsible wrapper for the single-job result body (deep link / restored view).
 * Mirrors the batch rows' toggle wording so both result views read as one system;
 * key it by job id so a new job starts expanded again.
 */
function CollapsibleJobResult({ children }: { children: ReactNode }) {
  const panelId = useId();
  const [open, setOpen] = useState(true);
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="mt-3 inline-flex items-center gap-1 text-smycles-label text-ink-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
      >
        <span aria-hidden="true" className={`transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}>
          ›
        </span>
        <span className="underline underline-offset-2">
          {open ? (
            <FormattedMessage id="service.page.bulkYaml.batchHide" defaultMessage="Hide result" />
          ) : (
            <FormattedMessage id="service.page.bulkYaml.batchOpen" defaultMessage="Show result" />
          )}
        </span>
      </button>
      <Collapse open={open}>
        <div id={panelId}>{children}</div>
      </Collapse>
    </>
  );
}

/**
 * Asynchronous drop for YAML import files. Extraction jobs (PDF/DOCX) are no longer started
 * here, but their deep links keep resolving for as long as their routing records live.
 *
 * The start mutation returns a durable job id; `fileImportJob` resolves the route
 * from that id alone, so a notification deep link restores the right view without
 * the client having to know which worker ran the file. For the YAML route the
 * richer `importJob` query plus its live subscription are layered on top.
 *
 * Deliberately absent (V1): any client-side compensation for a crashed worker. A
 * job whose pod died stays QUEUED/RUNNING, and this page will keep showing that
 * truthfully rather than faking a FAILED transition or a resume it cannot perform.
 */
export function BulkYamlImport() {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSystemAccess = useHasSystemPermission();
  const hasImportPermission = useHasPermission(Permission.WriteImport);
  const canImport = hasSystemAccess && hasImportPermission;
  const zoneRef = useRef<FileDropZoneHandle>(null);
  const jobId = searchParams.get("importJob");
  const batchId = searchParams.get("importBatch");
  const [files, setFiles] = useState<StagedFile[]>([]);
  const [rejections, setRejections] = useState<RejectedFile[]>([]);
  const [batchLimitExceeded, setBatchLimitExceeded] = useState(false);
  // The files of this batch whose jobs genesis already accepted. Everything in
  // this list is server-side: leaving the page loses nothing of it. Session
  // state on purpose - each job stays reachable through its notification.
  const [submitted, setSubmitted] = useState<SubmittedRun[]>([]);
  const [batchOutcomes, setBatchOutcomes] = useState<Record<string, BatchOutcome>>({});
  const reportBatchRow = (jobId: string, outcome: BatchOutcome) => {
    setBatchOutcomes((current) => (current[jobId] === outcome ? current : { ...current, [jobId]: outcome }));
  };
  // The server groups YAML jobs from one drop under this id. Keeping it while
  // the page stays open lets this view follow the same aggregate notification
  // that drives the top-bar progress instead of freezing after upload.
  const [activeBatch, setActiveBatch] = useState<ActiveBatch | null>(null);
  // True while the batch is still uploading file after file - the one window
  // in which leaving the page WOULD lose the files that have not gone up yet.
  const [uploading, setUploading] = useState(false);
  // The staged file whose job is currently active, kept for promoting a
  // single-file dry run to a real import (the browser holds no file across
  // jobs otherwise).
  const activeStagedRef = useRef<StagedFile | null>(null);
  const retryInFlightRef = useRef(false);
  const [options, setOptions] = useState<ImportRunOptions>(DEFAULT_IMPORT_OPTIONS);
  const [acceptedFileJob, setAcceptedFileJob] = useState<FileImportJobFieldsFragment | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [formatOpen, setFormatOpen] = useState(false);
  const [docAnchor, setDocAnchor] = useState<RequestedAnchor | null>(null);
  const [startFileImport] = useMutation(StartFileImportDocument);
  const restoredBatchQuery = useQuery(FileImportBatchDocument, {
    variables: { batchId: batchId ?? "" },
    skip: !batchId,
    fetchPolicy: "network-only",
  });
  const restoredBatchRuns: SubmittedRun[] = (restoredBatchQuery.data?.fileImportBatch ?? []).map((fileJob) => ({
    fileJob,
    // Restored rows are read-only: the browser no longer has the original file
    // bytes, so retry/promote actions must stay unavailable.
    staged: { name: fileJob.fileName, size: 0, mimeType: "", content: "", looksLikeYaml: true },
    options: DEFAULT_IMPORT_OPTIONS,
  }));
  const displayedSubmitted = submitted.length > 0 ? submitted : restoredBatchRuns;
  // Shares the format panel's cache entry for the query, so the count under the
  // drop zone and the one in the panel are read from one answer, not two.
  const { limits: specLimits } = useImportSpecification();

  // Step 1: resolve the route from the job id alone.
  //
  // Both queries poll declaratively via `pollInterval` rather than through
  // startPolling/stopPolling effects. Apollo hands back fresh function identities
  // on every render, so an effect keyed on them re-runs each render; with two such
  // queries feeding each other's re-renders that degenerates into a refetch loop.
  // A reactive interval (0 = stopped) expresses the same rule without the cycle.
  const fileJobQuery = useQuery(FileImportJobDocument, {
    variables: { jobId: jobId ?? "" },
    skip: !jobId,
    fetchPolicy: "network-only",
    // Deliberately NOT notifyOnNetworkStatusChange: nothing here reads the loading
    // flag, and opting in makes `data` blank out for the duration of every poll.
    // That would drop the resolved route back to null on each tick and flicker the
    // whole result view back to the empty drop zone between requests.
  });
  const queriedFileJob = fileJobQuery.data?.fileImportJob;
  const fileJob =
    (queriedFileJob?.id === jobId ? queriedFileJob : null) ??
    (acceptedFileJob?.id === jobId ? acceptedFileJob : null);
  const route = fileJob?.route;
  const isYamlRoute = route === FileImportRoute.YamlImport;
  const fileJobTerminal = isFileJobTerminal(fileJob?.status);
  // Only the source of truth polls: once the route is known to be YAML, `importJob`
  // carries strictly more, so polling both would just double the traffic.
  usePolling(fileJobQuery, Boolean(jobId) && !isYamlRoute && !fileJobTerminal);

  // Step 2 (YAML only): the rich job with counters, units, warnings and preview.
  const jobQuery = useQuery(ImportJobDocument, {
    variables: { jobId: jobId ?? "" },
    skip: !jobId || !isYamlRoute,
    fetchPolicy: "network-only",
    // Same reason as above: the progress view must not blank between polls.
  });
  const queriedJob = jobQuery.data?.importJob;
  const queryTerminal = isTerminal(queriedJob?.status);
  usePolling(jobQuery, Boolean(jobId) && isYamlRoute && !queryTerminal);

  const live = useSubscription(ImportJobEventsDocument, {
    variables: { jobId: jobId ?? "" },
    skip: !jobId || !isYamlRoute || queryTerminal,
  });
  // Every candidate is matched against `jobId` before any timestamp comparison
  // (see selectLatestImportJob): a query or subscription observable that
  // hasn't caught up to a jobId change yet (e.g. right after "New import")
  // can't leak the previous job's data into this render.
  const job = selectLatestImportJob(jobId, queriedJob, live.data?.importJobEvents);
  const terminal = isYamlRoute ? isTerminal(job?.status) : fileJobTerminal;
  const busy = starting || uploading || Boolean(fileJob && !terminal);

  // Reuse the inbox's authoritative batch snapshot and event stream. The query
  // gives us a race-free initial value; the subscription moves the bar
  // immediately, and the bounded poll is a fallback for a reconnect that missed
  // an event. No per-file polling fan-out is needed for a large drop.
  const batchNotificationQuery = useQuery(CockpitNotificationsDocument, {
    variables: NOTIFICATION_QUERY_VARIABLES,
    skip: !activeBatch,
    fetchPolicy: "cache-and-network",
  });
  const batchNotificationEvents = useSubscription(NotificationEventsDocument, {
    variables: { supported: SUPPORTED_NOTIFICATIONS },
    skip: !activeBatch,
  });
  const queriedBatchNotification = batchNotificationQuery.data?.notifications.find(
    (notification) => notification.entityId === activeBatch?.id,
  );
  const streamedNotification = batchNotificationEvents.data?.notificationEvents.notification;
  const streamedBatchNotification =
    streamedNotification?.entityId === activeBatch?.id ? streamedNotification : null;
  const batchNotification =
    isProgressAtLeastAsFresh(streamedBatchNotification?.progress, queriedBatchNotification?.progress)
      ? streamedBatchNotification ?? queriedBatchNotification
      : queriedBatchNotification;
  const batchProgress = batchNotification?.progress;
  usePolling(
    batchNotificationQuery,
    Boolean(activeBatch) &&
      (uploading || !batchProgress || batchProgress.done < Math.max(batchProgress.total, activeBatch?.accepted ?? 0)),
  );

  const steps = job
    ? buildImportPipelineSteps(job)
    : fileJob
      ? buildFileImportPipelineSteps(fileJob.status)
      : activeBatch
        ? batchPipelineSteps(uploading, batchProgress, activeBatch.accepted)
        : buildFileImportPipelineSteps(undefined);


  const setRunInUrl = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", "roster");
    if (id) next.set("importJob", id);
    else next.delete("importJob");
    next.delete("importBatch");
    setSearchParams(next, { replace: true });
  };

  const addFiles = async (fl: FileList) => {
    setStartError(null);
    setBatchLimitExceeded(false);
    // Client-side pre-check only (readable errors, no wasted mutation round
    // trip); genesis stays the authority on the type, the route, and both the
    // byte and entity-count ceilings. Document count is deliberately NOT counted
    // here (no `---` scanning, no YAML parser dependency): the server is the only
    // source that can parse the payload correctly.
    const { accepted, rejected } = await ingestFiles(fl, {
      maxBytes: MAX_YAML_IMPORT_BYTES,
      accept: ACCEPT,
    });
    // Every rejection is reported BY NAME, and the acceptable files still land:
    // a drop is one gesture for the user, so silently importing half of it
    // would read as "everything arrived".
    setRejections(rejected);
    if (accepted.length === 0) return;
    setFiles((prev) => {
      const next = [...prev];
      for (const selected of accepted) {
        // Same name and size twice in one batch is a double-drop, not intent.
        if (next.some((f) => f.name === selected.name && f.size === selected.size)) continue;
        if (next.length >= MAX_BATCH_FILES) {
          setBatchLimitExceeded(true);
          continue;
        }
        next.push({
          name: selected.name,
          size: selected.size,
          mimeType: selected.mimeType,
          content: selected.content,
          looksLikeYaml: acceptsFile(new File([], selected.name, { type: selected.mimeType }), YAML_ACCEPT),
        });
      }
      return next;
    });
  };

  /** Uploads one file and returns its accepted job, or null when genesis refused the start. */
  const start = async (
    staged: StagedFile,
    runOptions: ImportRunOptions,
    batch: { id: string } | null = null,
  ): Promise<FileImportJobFieldsFragment | null> => {
    setStarting(true);
    setStartError(null);
    activeStagedRef.current = staged;
    try {
      const result = await startFileImport({
        variables: {
          file: staged.content,
          fileName: staged.name,
          mimeType: staged.mimeType,
          // Files of one drop share one notification server-side, updated in
          // place with running counters instead of two messages per file.
          batch,
          // PDF/DOCX support the backend defaults only, so no options are sent for
          // them at all rather than sending values the extraction route ignores.
          options: staged.looksLikeYaml ? { mode: runOptions.mode, dryRun: runOptions.dryRun } : null,
        },
      });
      const started = result.data?.startFileImport;
      if (!started) throw new Error("Import job was not accepted");
      return started;
    } catch (error) {
      setStartError(
        errorClassification(error) === "FORBIDDEN"
          ? intl.formatMessage({
              id: "service.page.bulkYaml.forbidden",
              defaultMessage:
                "You lack permission to import this file. Import access is required; files containing people or reference data also require the corresponding editing permissions.",
            })
          : userErrorMessage(intl, error),
      );
      return null;
    } finally {
      setStarting(false);
    }
  };

  if (!canImport) return null;
  // Uploads every staged file back to back. Only the upload is client-side
  // work: startFileImport queues and returns, the bounded worker pool behind
  // it paces the actual processing. The moment the last mutation answered,
  // nothing about this batch depends on the page staying open - which is the
  // lesson of the first version, whose queue waited for each job to FINISH
  // and silently died with the tab.
  const runImport = async () => {
    if (busy || files.length === 0) return;
    const batch = files;
    // Genesis currently batches YAML notifications only; PDF/DOCX keep their extraction
    // notifications. The id groups accepted YAML jobs; Genesis derives the total from those
    // registrations, so a refused start can never leave progress waiting for a phantom file.
    const yamlCount = batch.filter((file) => file.looksLikeYaml).length;
    const batchRef = yamlCount > 1 ? { id: crypto.randomUUID() } : null;
    setFiles([]);
    setSubmitted([]);
    setBatchOutcomes({});
    setActiveBatch(null);
    setUploading(true);
    try {
      const done: SubmittedRun[] = [];
      let acceptedYaml = 0;
      for (const [index, staged] of batch.entries()) {
        const started = await start(staged, options, staged.looksLikeYaml ? batchRef : null);
        if (!started) {
          // A refused START (permissions, transport) would refuse the rest the
          // same way: hand everything not yet uploaded back to the drop zone.
          setFiles(batch.slice(index));
          break;
        }
        done.push({ fileJob: started, staged, options });
        if (staged.looksLikeYaml && batchRef) {
          acceptedYaml += 1;
          setActiveBatch({ id: batchRef.id, accepted: acceptedYaml });
        }
        setSubmitted([...done]);
        if (batch.length === 1) {
          // Keep the single file in the same batch row as multi-file drops. The
          // row is opened initially, so the detail remains visible while its
          // polled/subscribed status changes in place.
          setRunInUrl(started.id);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const retryBatchRun = async (run: SubmittedRun) => {
    if (retryInFlightRef.current) return;
    retryInFlightRef.current = true;
    try {
      // A retry is intentionally a standalone job. Reusing the completed
      // batch id would advance its immutable counters beyond the advertised
      // total; the original batch summary remains an honest historical result.
      const started = await start(run.staged, run.options);
      if (!started) return;
      setSubmitted((current) =>
        current.map((candidate) =>
          candidate.fileJob.id === run.fileJob.id ? { ...run, fileJob: started } : candidate,
        ),
      );
    } finally {
      retryInFlightRef.current = false;
    }
  };

  // Shared by "New import" (success) and "Fix file and retry" (failure): both
  // need the same clean slate. Drop the finished job, unsubscribe (jobId ->
  // null tears down the query/subscription via their skip/variables), and
  // clear the file picker so a new run starts from scratch.
  const reset = (nextOptions: ImportRunOptions = DEFAULT_IMPORT_OPTIONS) => {
    setAcceptedFileJob(null);
    setStartError(null);
    setRejections([]);
    setBatchLimitExceeded(false);
    setFiles([]);
    setSubmitted([]);
    setBatchOutcomes({});
    setActiveBatch(null);
    activeStagedRef.current = null;
    setOptions(nextOptions);
    setRunInUrl(null);
  };

  const promotableRuns = submitted.filter(
    (run) => run.options.dryRun && batchOutcomes[run.fileJob.id] === "success",
  );

  /** Re-run every successfully validated file that has not been promoted yet. */
  const promoteBatchToRealImport = async () => {
    if (uploading || starting || submitted.length === 0) return;
    const candidates = promotableRuns;
    if (candidates.length === 0) return;
    // A single promoted file starts without a batch registration, mirroring
    // runImport: genesis batches only multi-file YAML notifications.
    const batchRef = candidates.length > 1 ? { id: crypto.randomUUID() } : null;
    // Stop following the completed validation batch before registering the
    // real-import batch. Its accepted count grows only after Genesis confirms
    // a start, so a refused mutation can never leave progress waiting for a
    // job that does not exist server-side.
    setActiveBatch(null);
    let accepted = 0;
    for (const run of candidates) {
      const realOptions = { ...run.options, dryRun: false };
      const started = await start(run.staged, realOptions, batchRef);
      if (!started) continue;
      if (batchRef) {
        accepted += 1;
        setActiveBatch({ id: batchRef.id, accepted });
      }
      setSubmitted((current) => current.map((candidate) =>
        candidate.fileJob.id === run.fileJob.id ? { ...candidate, fileJob: started, options: realOptions } : candidate));
      if (candidates.length === 1) setRunInUrl(started.id);
    }
  };

  const displayError =
    startError ??
    (fileJobQuery.error
      ? userErrorMessage(intl, fileJobQuery.error)
      : jobQuery.error
        ? userErrorMessage(intl, jobQuery.error)
        : null);

  const openDocumentation = (anchor: string) => {
    setFormatOpen(true);
    // The guide chapters are collapsed cards; the target chapter opens itself
    // when it sees this request, otherwise the scroll would land on a closed box.
    setDocAnchor((current) => ({ anchor, nonce: (current?.nonce ?? 0) + 1 }));
    const hash = `#${anchor}`;
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
    // Scrolling happens in the guide chapter that answers the anchor (it also
    // has to open itself first); see GuideSection in ImportSpecificationPanel.
  };

  const showYamlOptions = files.length === 0 || files.some((f) => f.looksLikeYaml);
  const canPromoteValidatedBatch =
    !options.dryRun && promotableRuns.length > 0;

  return (
    <div id="yaml-import" className="box bg-surface p-6">
      <BoxHeader
        title={intl.formatMessage({ id: "service.page.bulkYaml.title", defaultMessage: "File import" })}
        variant="hero"
      />
      <div className="smycles-field-meta leading-snug text-ink">
        <FormattedMessage
          id="service.page.bulkYaml.desc"
          defaultMessage="Import a multi-document YAML payload. The job continues when you leave the page."
        />
      </div>

      <ImportPipelineBar steps={steps} intl={intl} />

      {/* Right where the eye is after pressing start - not at the very bottom,
          where an expanded format panel pushed it out of sight. */}
      {displayError ? (
        <div id="yaml-import-error" role="alert" className="mb-3 text-smycles-meta text-red-600">
          {displayError}
        </div>
      ) : null}

      {fileJob && displayedSubmitted.length === 0 ? (
        <section aria-live="polite">
          <div className="rounded-md bg-ink/[0.035] p-4 ring-1 ring-ink/10">
            <div className="flex flex-wrap items-start justify-between gap-2">
              {isYamlRoute ? (
                <YamlJobBody
                  job={job}
                  fileName={fileJob.fileName}
                  intl={intl}
                  onOpenDocumentation={openDocumentation}
                />
              ) : (
                <ExtractionJobBody fileJob={fileJob} />
              )}

              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <RouteBadge route={fileJob.route} intl={intl} />
                {!terminal ? (
                  <span className="inline-flex shrink-0 items-center gap-2 text-smycles-label text-ink-muted">
                    <span className="pulse-dot h-2 w-2 rounded-full bg-accent" />
                    <FormattedMessage id="service.page.bulkYaml.background" defaultMessage="Runs in background" />
                  </span>
                ) : null}
              </div>
            </div>

            {job &&
            job.status === ImportJobStatus.Running &&
            job.stage === ImportJobStage.Importing &&
            job.totalDocuments > 0 ? (
              <div
                className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-ink/10"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={job.totalDocuments}
                aria-valuenow={job.processedDocuments}
              >
                <div
                  className="h-full rounded-full bg-accent transition-[width] duration-300"
                  style={{ width: `${Math.min(100, (job.processedDocuments / job.totalDocuments) * 100)}%` }}
                />
              </div>
            ) : null}

            {job && terminal ? (
              <CollapsibleJobResult key={job.id}>
                {/* Keyed by job: the deleted-units state must not survive into
                    the next run, which can reuse the same fixed entity ids. */}
                <ImportUnitBreakdown
                  key={job.id}
                  idPrefix={`import-job-${job.id}`}
                  units={job.units}
                  personActions={job.personActions}
                  dryRun={job.dryRun}
                  totalUnits={job.totalUnits}
                  succeededUnits={job.succeededUnits}
                  failedUnits={job.failedUnits}
                  skippedUnits={job.skippedUnits}
                  onOpenDocumentation={openDocumentation}
                />
                <ImportWarnings warnings={job.warnings} />
                <ImportPersonActions personActions={job.personActions} dryRun={job.dryRun} />
              </CollapsibleJobResult>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* No promote button here: turning a finished validation into a real
                import happens via the mode toggle plus the primary button below,
                for one file exactly like for a batch. */}
            {terminal && fileJob.status !== FileImportJobStatus.Failed && job?.status !== ImportJobStatus.Failed ? (
              <Button variant="secondary" onClick={() => reset()}>
                <FormattedMessage id="service.page.bulkYaml.newRun" defaultMessage="Choose another file" />
              </Button>
            ) : terminal ? (
              <Button variant="secondary" onClick={() => reset()}>
                <FormattedMessage id="service.page.bulkYaml.retryBtn" defaultMessage="Fix file and retry" />
              </Button>
            ) : (
              <div className="text-smycles-meta text-ink-muted">
                <FormattedMessage
                  id="service.page.bulkYaml.leaveHint"
                  defaultMessage="You may leave this page. A notification links back to this result."
                />
              </div>
            )}
          </div>
        </section>
      ) : (
        <>
          {/* The options define what the already visible drop area will do, so
              they stay above both its live status and the drop itself. */}
          {showYamlOptions ? (
            <ImportOptionsPicker value={options} onChange={setOptions} disabled={starting} />
          ) : null}

          {uploading || displayedSubmitted.length > 0 ? (
            <section aria-live="polite" className="mb-4 mt-4 rounded-md bg-ink/[0.035] p-4 ring-1 ring-ink/10">
              {uploading ? (
                <div className="flex items-center gap-2 text-smycles-meta text-ink">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <FormattedMessage
                    id="service.page.bulkYaml.batchUploading"
                    defaultMessage="Uploading file {n, number}… ({done, number} started)"
                    values={{ n: displayedSubmitted.length + 1, done: displayedSubmitted.length }}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-smycles-meta text-ink">
                  {activeBatch &&
                  (!batchProgress ||
                    batchProgress.done < Math.max(batchProgress.total, activeBatch.accepted)) ? (
                    <span className="inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : null}
                  {batchNotification?.body.localized ? (
                    batchNotification.body.localized
                  ) : (
                    <FormattedMessage
                      id="service.page.bulkYaml.batchDone"
                      defaultMessage="All {n, number} files are uploaded. Processing continues in the background - you may leave this page."
                      values={{ n: displayedSubmitted.length }}
                    />
                  )}
                </div>
              )}
              {!uploading && batchProgress ? (
                <div
                  className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink/10"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={batchProgress.total}
                  aria-valuenow={batchProgress.done}
                  aria-label={batchNotification?.title.localized ?? undefined}
                >
                  <div
                    className="h-full rounded-full bg-accent transition-[width] duration-300"
                    style={{
                      width: `${batchProgress.total > 0 ? Math.min(100, (batchProgress.done / batchProgress.total) * 100) : 0}%`,
                    }}
                  />
                </div>
              ) : null}
              {!uploading && batchProgress ? (
                <div className="mt-1 text-smycles-label tabular-nums text-ink">
                  <FormattedMessage id="common.progressCount" defaultMessage="{done, number} / {total, number}" values={{ done: batchProgress.done, total: batchProgress.total }} />
                </div>
              ) : null}
              {displayedSubmitted.length > 0 ? (
                <ul className="mt-2 flex flex-col gap-1">
                  {displayedSubmitted.map((run) =>
                    uploading ? (
                      <li key={run.fileJob.id} className="flex items-center gap-2 py-1 text-smycles-meta">
                        <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                        <span className="truncate text-ink">{run.fileJob.fileName}</span>
                      </li>
                    ) : (
                      <BatchRunResult
                        key={run.fileJob.id}
                        run={run}
                        onRetry={retryBatchRun}
                        onOpenDocumentation={openDocumentation}
                        onReport={reportBatchRow}
                        openInitially={displayedSubmitted.length === 1}
                        readOnly={submitted.length === 0}
                      />
                    ),
                  )}
                </ul>
              ) : null}
            </section>
          ) : null}

          <FileDropZone
            id="yaml-import-file-input-zone"
            ref={zoneRef}
            inputId="yaml-import-file-input"
            variant="zone"
            accept={ACCEPT}
            disabled={starting}
            clickToOpen={files.length === 0}
            onFiles={(fl) => void addFiles(fl)}
            label={
              <FormattedMessage
                id="service.page.bulkYaml.zone"
                defaultMessage="Drag YAML files here or click to add"
              />
            }
            hint={
              <FormattedMessage
                id="service.page.bulkYaml.hint"
                defaultMessage="YAML or YML · each file becomes its own import job"
              />
            }
          >
            {files.length > 0 ? (
              // Same grid as the CV import's file list: the tile is a 4:3 card
              // (@adr SPARK-0022-file-drop-fields@2) and fills whatever it is given, so it needs a column
              // to sit in.
              <div className="grid grid-cols-3 gap-2 p-3 @sm:grid-cols-4 @lg:grid-cols-6">
                {files.map((f) => (
                  <FilePreviewTile
                    key={`${f.name}-${f.size}`}
                    name={f.name}
                    sizeBytes={f.size}
                    onRemove={
                      starting
                        ? undefined
                        : () => setFiles((prev) => prev.filter((x) => !(x.name === f.name && x.size === f.size)))
                    }
                  />
                ))}
              </div>
            ) : null}
          </FileDropZone>

          {/* Two numbers with two owners. The size is Spark's transport limit,
              formatted from the same constant the check reads; genesis's own,
              larger format ceiling belongs to the format panel below, never to
              the drop zone. The record count is genesis's alone, so it is
              quoted from the loaded specification or, until that answer is in,
              left unsaid rather than guessed. */}
          <div id="yaml-import-limits" className="mt-2 text-smycles-label text-ink-muted">
            {specLimits ? (
              <FormattedMessage
                id="service.page.bulkYaml.limitsNote"
                defaultMessage="Maximum {docs, number} records per import · up to {files, number} files per batch · at most {size} per file."
                values={{
                  docs: specLimits.maxTopLevelDocuments,
                  files: MAX_BATCH_FILES,
                  size: formatMegabytes(MAX_YAML_IMPORT_BYTES),
                }}
              />
            ) : (
              <FormattedMessage
                id="service.page.bulkYaml.limitsNoteSizeOnly"
                defaultMessage="Up to {files, number} files per batch · at most {size} per file."
                values={{ files: MAX_BATCH_FILES, size: formatMegabytes(MAX_YAML_IMPORT_BYTES) }}
              />
            )}
          </div>
          <div className="mt-1 text-smycles-label text-ink-muted">
            <FormattedMessage
              id="service.page.bulkYaml.routingHint"
              defaultMessage="Only YAML and YML files are accepted."
            />
          </div>

          {batchLimitExceeded ? (
            <div id="yaml-import-batch-limit" role="alert" className="mt-2 text-smycles-meta text-red-600">
              <FormattedMessage
                id="service.page.bulkYaml.batchLimit"
                defaultMessage="At most {max, number} files can be started in one batch. Split larger selections into multiple batches."
                values={{ max: MAX_BATCH_FILES }}
              />
            </div>
          ) : null}

          {rejections.length > 0 ? (
            <div id="yaml-import-rejections" className="mt-2 flex flex-col gap-0.5 text-smycles-meta text-red-600">
              {/* Named per file: a drop is one gesture, so anything it silently
                  lost would read as imported. The acceptable files of the same
                  drop are staged above regardless. */}
              {rejections.some((r) => r.reason === "unsupportedType") ? (
                <span id="yaml-import-rejected-type">
                <FormattedMessage
                  id="service.page.bulkYaml.rejectedType"
                  defaultMessage="Unsupported file type ({names}). Use .yaml or .yml."
                  values={{
                    names: rejections
                      .filter((r) => r.reason === "unsupportedType")
                      .map((r) => r.file.name)
                      .join(", "),
                  }}
                />
                </span>
              ) : null}
              {rejections.some((r) => r.reason === "tooLarge") ? (
                <span id="yaml-import-rejected-toolarge">
                <FormattedMessage
                  id="service.page.bulkYaml.rejectedTooLarge"
                  defaultMessage="Too large (over {max} per file): {names}"
                  values={{
                    max: formatMegabytes(MAX_YAML_IMPORT_BYTES),
                    names: rejections
                      .filter((r) => r.reason === "tooLarge")
                      .map((r) => `${r.file.name} (${formatMegabytes(r.file.size)})`)
                      .join(", "),
                  }}
                />
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="mt-3">
            <Button
              id="yaml-import-start"
              variant="primary"
              disabled={starting || uploading || (canPromoteValidatedBatch ? false : files.length === 0)}
              onClick={() => void (canPromoteValidatedBatch ? promoteBatchToRealImport() : runImport())}
            >
              {starting ? (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <FormattedMessage id="service.page.bulkYaml.starting" defaultMessage="Starting …" />
                </span>
              ) : canPromoteValidatedBatch ? (
                promotableRuns.length > 1 ? (
                  <FormattedMessage
                    id="service.page.bulkYaml.importBtnMany"
                    defaultMessage="Run import for {n, number} files"
                    values={{ n: promotableRuns.length }}
                  />
                ) : (
                  <FormattedMessage id="service.page.bulkYaml.importBtn" defaultMessage="Run import" />
                )
              ) : showYamlOptions && options.dryRun ? (
                // The button always names the action that is actually about to run,
                // and how many jobs it starts.
                files.length > 1 ? (
                  <FormattedMessage
                    id="service.page.bulkYaml.startDryMany"
                    defaultMessage="Run validation for {n, number} files"
                    values={{ n: files.length }}
                  />
                ) : (
                  <FormattedMessage id="service.page.bulkYaml.startDry" defaultMessage="Run validation" />
                )
              ) : files.length > 1 ? (
                <FormattedMessage
                  id="service.page.bulkYaml.importBtnMany"
                  defaultMessage="Run import for {n, number} files"
                  values={{ n: files.length }}
                />
              ) : (
                <FormattedMessage id="service.page.bulkYaml.importBtn" defaultMessage="Run import" />
              )}
            </Button>
          </div>

        </>
      )}
      <ImportSpecificationPanel
        open={formatOpen}
        onToggle={() => setFormatOpen((value) => !value)}
        requestedAnchor={docAnchor}
      />
    </div>
  );
}
